/**
 * Constants for DynamicCriteriaGenerator
 *
 * All type metadata is derived from TYPE_REGISTRY in typeRegistry.ts.
 * This file provides backward-compatible exports and allowed type presets.
 */

import { CriteriaComponentType } from 'src/redux/slices/aiListGeneratorSlice';
import {
  TYPE_REGISTRY,
  ALL_TYPES,
  STATIC_TYPES,
  getTypeLabel,
  getMenuItems,
} from './typeRegistry';

// === Re-exports from typeRegistry ===
export { TYPE_REGISTRY, getTypeLabel } from './typeRegistry';
export type { TypeMetadata, ComponentTypeClass } from './typeRegistry';

// === Legacy Exports (for backward compatibility) ===

/**
 * @deprecated Use TYPE_REGISTRY from typeRegistry.ts instead
 */
export const CRITERIA_TYPE_CONFIG: Record<
  CriteriaComponentType,
  {
    dataField: string;
    isStatic?: boolean;
  }
> = Object.fromEntries(
  Object.entries(TYPE_REGISTRY).map(([type, meta]) => [
    type,
    {
      dataField: meta.reduxDataField,
      isStatic: meta.typeClass === 'static' ? true : undefined,
    },
  ])
) as Record<CriteriaComponentType, { dataField: string; isStatic?: boolean }>;

/**
 * All criteria component types
 */
export const ALL_CRITERIA_TYPES = ALL_TYPES;

/**
 * Static component types that don't require data selection
 */
export const STATIC_COMPONENT_TYPES = STATIC_TYPES;

// === Allowed Type Presets ===

/**
 * Default allowed component types for DynamicCriteriaGenerator
 * Excludes: this_provider, this_site (context-specific), specialties
 */
export const DEFAULT_ALLOWED_TYPES: CriteriaComponentType[] = [
  'codes',
  'code_set',
  'crm_status',
  'education_focus',
  'education_program_type',
  'educational_institution',
  'entity_attribute',
  'entity_type',
  'filter_set',
  'open_payments',
  'open_payments_category',
  'payer_name',
  'payer_type',
  'provider_cohort',
  'provider_list',
  'providers',
];

/**
 * Strategy configuration allowed types (includes this_provider, this_site)
 */
export const STRATEGY_ALLOWED_TYPES: CriteriaComponentType[] = [
  'codes',
  'code_set',
  'crm_status',
  'education_focus',
  'education_program_type',
  'educational_institution',
  'entity_attribute',
  'entity_type',
  'filter_set',
  'open_payments',
  'open_payments_category',
  'payer_name',
  'payer_type',
  'provider_cohort',
  'provider_list',
  'providers',
  'specialties',
  'this_provider',
  'this_site',
];

/**
 * All types except crm_status
 */
export const ALL_TYPES_EXCEPT_CRM_STATUS: CriteriaComponentType[] =
  STRATEGY_ALLOWED_TYPES.filter((type) => type !== 'crm_status');

/**
 * Patient journey index event allowed types (restricted set)
 */
export const PATIENT_JOURNEY_INDEX_ALLOWED_TYPES: CriteriaComponentType[] = [
  'code_set',
  'provider_cohort',
  'this_provider',
  'this_site',
];

/**
 * Patient journey target event allowed types (restricted set)
 */
export const PATIENT_JOURNEY_TARGET_ALLOWED_TYPES: CriteriaComponentType[] = [
  'code_set',
  'filter_set',
  'provider_cohort',
  'entity_type',
];

/**
 * Maps strategy types to their allowed criteria component types.
 * Used as a fallback when the config doesn't specify allowed types.
 */
export const STRATEGY_TYPE_ALLOWED_TYPES_MAP: Record<
  string,
  CriteriaComponentType[]
> = {
  // Target strategies
  SCOUT_LIST: DEFAULT_ALLOWED_TYPES,
  FRESH_FACES: [], // Uses custom configuration, not DCG
  // Engage strategies
  REFERRALS: STRATEGY_ALLOWED_TYPES,
  NOT_REFERRING: STRATEGY_ALLOWED_TYPES,
  PATIENT_JOURNEY: [], // Uses separate index/target event editors
  TREATING_VOLUME: ALL_TYPES_EXCEPT_CRM_STATUS,
};

/**
 * Order in which to display result sections in the slash menu
 */
export const SECTION_ORDER: CriteriaComponentType[] = [
  'this_provider',
  'this_site',
  'providers',
  'codes',
  'code_set',
  'payer_type',
  'payer_name',
  'filter_set',
  'provider_list',
  'provider_cohort',
  'crm_status',
  'open_payments',
  'open_payments_category',
  'entity_attribute',
  'educational_institution',
];

/**
 * Minimum characters typed after "/" before the slash menu searches
 */
export const MIN_SEARCH_LENGTH = 2;

// === Menu Items ===

export interface SlashMenuItem {
  type: CriteriaComponentType;
  label: string;
  color: string;
  description: string;
  show?: boolean;
}

/**
 * Menu items derived from TYPE_REGISTRY
 * Sorted alphabetically by label
 */
export const SLASH_MENU_ITEMS: SlashMenuItem[] = getMenuItems(ALL_TYPES).map(
  (item) => ({ ...item, show: true })
);

/**
 * Gets a human-readable label for a component type (for tooltips)
 * @deprecated Use getTypeLabel from typeRegistry.ts instead
 */
export const getComponentTypeLabel = getTypeLabel;
